<template>
    <div class="suggestion-row">
        <img
            v-if="suggestion.icon"
            :src="suggestion.icon"
            class="suggestion-row__icon"
            alt=""
            :data-test-id="`${dataTestIdPrefix}-icon`"
        />
        <span
            v-else
            class="suggestion-row__icon suggestion-row__icon--placeholder"
            aria-hidden="true"
            :data-test-id="`${dataTestIdPrefix}-icon-placeholder`"
        />

        <span class="suggestion-row__text">
            <span
                class="suggestion-row__name"
                :class="{ 'suggestion-row__name--unselectable': isNotAvailable }"
                :data-test-id="`${dataTestIdPrefix}-name`"
                >{{ suggestion.name }}</span
            >
            <span
                v-if="statusLabel"
                class="suggestion-row__status"
                :data-test-id="`${dataTestIdPrefix}-status`"
                >{{ statusLabel }}</span
            >
            <span
                v-else-if="detailLine"
                class="suggestion-row__details"
                :data-test-id="`${dataTestIdPrefix}-details`"
                >{{ detailLine }}</span
            >
        </span>

        <slot name="suffix"></slot>
    </div>
</template>

<script>
import { suggestionStates } from '@/constants/suggestion-statuses'

// `details` is an unlabeled positional array from the backend; render it as-is.
export default {
    name: 'SuggestionRow',

    props: {
        suggestion: {
            required: true,
            type: Object,
        },
        dataTestIdPrefix: {
            required: true,
            type: String,
        },
        status: {
            required: false,
            type: Object,
            default: null,
        },
    },

    computed: {
        isNotAvailable() {
            return this.status?.state === suggestionStates.NOT_AVAILABLE
        },

        statusLabel() {
            return this.status?.label ?? ''
        },

        detailLine() {
            const { details } = this.suggestion

            if (!Array.isArray(details) || !details.length) {
                return ''
            }

            return details.filter((detail) => detail).join(' · ')
        },
    },
}
</script>

<style lang="scss" scoped>
.suggestion-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;

    &__icon {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        object-fit: contain;

        &--placeholder {
            border-radius: 50%;
            background: var(--p-surface-200, #e5e7eb);
        }
    }

    &__text {
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    // Colours are explicit: the body sets `color: #fff`, but this row renders
    // on light surfaces, so inheriting would give white on white.
    &__name {
        font-weight: 500;
        line-height: 1.25;
        color: var(--p-text-color, #212628);

        &--unselectable {
            color: var(--p-text-muted-color, #6b7280);
            text-decoration: line-through;
        }
    }

    &__status {
        font-size: 0.8rem;
        color: var(--p-text-muted-color, #6b7280);
        line-height: 1.3;
        font-style: italic;
    }

    &__details {
        font-size: 0.8rem;
        color: var(--p-text-muted-color, #6b7280);
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

// White text on the green highlighted option (see TypeaheadField's OPTION_TOKENS).
.p-autocomplete-option.p-focus {
    .suggestion-row__name,
    .suggestion-row__status,
    .suggestion-row__details {
        color: #fff;
    }
}
</style>
